import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { User, Settings, LogOut, LogIn, ChevronDown, Mail, Briefcase as BriefcaseIcon, Check, Compass, Palette, Lock, Keyboard, LayoutDashboard, GraduationCap } from "lucide-react";
import { logAdminActivity } from "@/utils/adminActivity";
import { useNavigate, useLocation } from "react-router-dom";
import { useTokenValidation, clearValidationCache, setValidationCache } from "@/hooks/useTokenValidation";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useProductEntitlements } from "@/hooks/useProductEntitlements";
import { useProfileSwitcherSafe } from "@/contexts/ProfileSwitcherContext";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { calculateProfileCompletion } from "@/utils/profileCompletionCalculator";
import { handleSignOut as utilHandleSignOut } from "@/utils/auth";
import { useProfile } from "@/contexts/UserProfileContext";
import { useUserRoles } from "@/hooks/useUserRoles";
import { RoleBadge, roleConfig } from "@/components/RoleSwitcher";
import { cn } from "@/lib/utils";

// Product Switch Section Component
interface ProductSwitchSectionProps {
  canAccessAdmin: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  navigate: (path: string) => void;
  isValidUser: boolean;
}

const ProductSwitchSection: React.FC<ProductSwitchSectionProps> = ({
  canAccessAdmin,
  isAdmin,
  isSuperAdmin,
  navigate,
  isValidUser,
}) => {
  const { hasProduct, isLoading } = useProductEntitlements();
  const { activeProduct, setActiveProduct, setShowAccessOverlay } = useProfileSwitcherSafe();

  // Admins and super admins have full access to both products
  const hasCareerPathAccess = canAccessAdmin || isSuperAdmin || isAdmin || hasProduct('career_path');
  const hasCareerStudioAccess = canAccessAdmin || isSuperAdmin || isAdmin || hasProduct('career_studio');

  const handleProductSwitch = useCallback((product: 'career_path' | 'career_studio') => {
    const hasAccess = product === 'career_path' ? hasCareerPathAccess : hasCareerStudioAccess;
    
    if (hasAccess) {
      setActiveProduct?.(product);
      navigate(product === 'career_path' ? '/compass' : '/studio');
    } else {
      // Show upgrade overlay for locked products
      setShowAccessOverlay?.(true, product);
    }
  }, [hasCareerPathAccess, hasCareerStudioAccess, navigate, setActiveProduct, setShowAccessOverlay]);

  // Keyboard shortcuts for product switching: Alt+1 (Career Path), Alt+2 (Career Studio)
  useEffect(() => {
    if (!isValidUser) return;

    const handleProductShortcut = (e: KeyboardEvent) => {
      // Alt+1 = Career Path
      if (e.altKey && e.key === '1') {
        e.preventDefault();
        handleProductSwitch('career_path');
      }
      // Alt+2 = Career Studio
      if (e.altKey && e.key === '2') {
        e.preventDefault();
        handleProductSwitch('career_studio');
      }
    };

    window.addEventListener('keydown', handleProductShortcut);
    return () => window.removeEventListener('keydown', handleProductShortcut);
  }, [isValidUser, handleProductSwitch]);

  if (isLoading) return null;

  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="text-xs text-muted-foreground font-normal px-2 py-1.5 flex items-center justify-between">
        <span>SWITCH PRODUCT</span>
        <span className="text-[10px] text-muted-foreground/70 flex items-center gap-1">
          <Keyboard className="h-3 w-3" />
          Alt+1/2
        </span>
      </DropdownMenuLabel>
      <div className="px-1 pb-1">
        {/* Career Path */}
        <DropdownMenuItem
          onClick={() => handleProductSwitch('career_path')}
          className={cn("cursor-pointer gap-3", activeProduct === 'career_path' && "bg-compass/5")}
        >
          <div className="p-1.5 rounded bg-compass/10 text-compass">
            <Compass className="h-3.5 w-3.5" />
          </div>
          <span className="text-sm flex-1">Career Path</span>
          {activeProduct === 'career_path' && <Check className="h-4 w-4 text-compass" />}
          {!hasCareerPathAccess && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
        </DropdownMenuItem>
        
        {/* Career Studio */}
        <DropdownMenuItem
          onClick={() => handleProductSwitch('career_studio')}
          className={cn("cursor-pointer gap-3", activeProduct === 'career_studio' && "bg-studio/5")}
        >
          <div className="p-1.5 rounded bg-studio/10 text-studio">
            <Palette className="h-3.5 w-3.5" />
          </div>
          <span className="text-sm flex-1">Career Studio</span>
          {activeProduct === 'career_studio' && <Check className="h-4 w-4 text-studio" />}
          {!hasCareerStudioAccess && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
        </DropdownMenuItem>
      </div>
    </>
  );
};

export const UserProfileMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { result, isLoading: tokenLoading } = useTokenValidation();
  const { profile, isLoading: profileLoading } = useProfile();
  const { roles, activeRole, setActiveRole, isAdmin, isSuperAdmin, isLoading: rolesLoading, refetch: refetchRoles } = useUserRoles(profile?.id);
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  const [profileCompletion, setProfileCompletion] = useState<any>(null);

  const isAdminView = location.pathname.startsWith('/admin');
  
  // Simple loading: only block on token validation, NOT on profile/roles
  const isLoading = tokenLoading;
  
  // Admin access: if token says admin, show admin controls immediately
  const canAccessAdmin = result?.isAdmin === true;
  
  // Filter to elevated roles for role switcher
  const elevatedRoles = ['super_admin', 'admin', 'it_admin', 'platform_admin', 'marketing', 'product_manager', 'intern'];
  const adminRoles = roles.filter(role => elevatedRoles.includes(role));
  
  // Force role refetch when admin token exists but no roles are loaded
  useEffect(() => {
    const hasAdminToken = !!localStorage.getItem('admin_auth_token');
    const hasNoRoles = !rolesLoading && adminRoles.length === 0;
    const hasProfileId = !!profile?.id || !!localStorage.getItem('sirppi_profile_id');
    
    if (hasAdminToken && hasNoRoles && hasProfileId && canAccessAdmin) {
      console.log('[UserProfileMenu] Admin token exists but no roles - forcing refetch');
      // Set admin navigation grace period to skip stale cache
      localStorage.setItem('admin_navigation_grace', Date.now().toString());
      refetchRoles();
    }
  }, [profile?.id, rolesLoading, adminRoles.length, canAccessAdmin, refetchRoles]);

  // Smart display name - never show raw email
  const displayName = useMemo(() => {
    if (profile?.full_name) return profile.full_name;
    if (profileData?.full_name) return profileData.full_name;
    if (result?.userName) return result.userName;
    if (result?.userEmail) {
      // Extract name from email: john.doe@email.com -> "John Doe"
      const namePart = result.userEmail.split('@')[0];
      return namePart
        .split(/[._-]/)
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return 'User';
  }, [profile?.full_name, profileData?.full_name, result?.userName, result?.userEmail]);

  // Debug authentication state
  useEffect(() => {
    console.log('[UserProfileMenu] Auth state:', {
      isLoading,
      tokenLoading,
      profileLoading,
      rolesLoading,
      profileId: profile?.id,
      roles,
      isAdmin,
      isSuperAdmin,
      canAccessAdmin,
      isValid: result?.isValid,
      userEmail: result?.userEmail,
      resultIsAdmin: result?.isAdmin,
      userToken: localStorage.getItem('user_auth_token') ? 'exists' : 'missing',
      adminToken: localStorage.getItem('admin_auth_token') ? 'exists' : 'missing',
    });
  }, [result, isLoading, tokenLoading, profileLoading, rolesLoading, profile?.id, roles, isAdmin, isSuperAdmin, canAccessAdmin]);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!result?.userEmail) {
        console.log('[UserProfileMenu] Skipping profile fetch - no email:', result?.userEmail);
        return;
      }

      console.log('[UserProfileMenu] Fetching profile for:', result.userEmail);

      try {
        // Use edge function pattern with localStorage token
        const token = localStorage.getItem('user_auth_token') || localStorage.getItem('admin_auth_token');
        
        if (!token) {
          console.warn('[UserProfileMenu] No token found in localStorage');
          return;
        }

        console.log('[UserProfileMenu] Invoking get-user-profile edge function...');
        const { data, error } = await supabase.functions.invoke('get-user-profile', {
          body: { token }
        });

        if (error) {
          console.error('[UserProfileMenu] Edge function error:', error);
          return;
        }

        if (!data?.profile) {
          console.warn('[UserProfileMenu] No profile data returned');
          return;
        }

        console.log('[UserProfileMenu] ✅ Profile loaded:', data.profile.full_name || data.profile.email);
        setProfileData(data.profile);
        const completion = calculateProfileCompletion(data.profile);
        setProfileCompletion(completion);
      } catch (error) {
        console.error('[UserProfileMenu] Exception:', error);
      }
    };

    fetchProfileData();
  }, [result?.userEmail]);

  // Handle view switching - preserve caches when switching TO admin
  const handleViewSwitch = useCallback(async (targetPath: string) => {
    const fromView = isAdminView ? 'admin' : 'user';
    const toView = targetPath.startsWith('/admin') ? 'admin' : 'user';
    
    // Log the view switch for security auditing
    logAdminActivity({
      activityType: 'view_mode_switched',
      description: `Switched from ${fromView} view to ${toView} view`,
      resourceType: 'user_session',
      resourceId: result?.userEmail || 'unknown',
      beforeState: { viewMode: fromView, path: location.pathname },
      afterState: { viewMode: toView, path: targetPath }
    }).catch(err => console.warn('[UserProfileMenu] Failed to log view switch:', err));
    
    if (targetPath.startsWith('/admin')) {
      // Set navigation grace period
      localStorage.setItem('admin_navigation_grace', Date.now().toString());
      
      // Seed admin validation cache with current user session data
      // so AccessGate has immediate data to work with
      if (result?.isValid) {
        setValidationCache('admin', result as any);
        console.log('[UserProfileMenu] Seeded admin cache with user session data');
      }
      // Do NOT clear role caches - let stale-while-revalidate handle background refresh
    }
    
    // Clear validation cache when LEAVING admin view
    if (toView === 'user' && !targetPath.startsWith('/admin')) {
      clearValidationCache();
      localStorage.removeItem('active_admin_role');
      localStorage.removeItem('admin_navigation_grace');
    }
    
    navigate(targetPath);
  }, [navigate, isAdminView, result?.userEmail, result?.isValid, location.pathname]);

  // Keyboard shortcut for admin toggle: Alt+A
  useEffect(() => {
    if (!canAccessAdmin) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && e.key === 'a') {
        e.preventDefault();
        handleViewSwitch(isAdminView ? '/' : '/admin');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAdminView, handleViewSwitch, canAccessAdmin]);

  // Show loading state FIRST - don't show Sign In while still validating
  if (isLoading) {
    console.log('[UserProfileMenu] Loading authentication...');
    return (
      <Button variant="ghost" disabled className="gap-2 rounded-2xl">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        Loading...
      </Button>
    );
  }

  // If not authenticated, show Sign In button
  if (!result?.isValid) {
    console.log('[UserProfileMenu] Showing Sign In button - not authenticated');
    return (
      <Button 
        variant="ghost" 
        onClick={() => navigate('/login')}
        className="gap-2 rounded-2xl hover:bg-muted/50 transition-all"
        aria-label="Sign in to your account"
      >
        <LogIn className="h-4 w-4" />
        Sign In
      </Button>
    );
  }

  const getUserInitials = () => {
    const name = profileData?.full_name || result?.userName || '';
    if (!name) return 'U';
    
    const nameParts = name.trim().split(' ');
    if (nameParts.length === 1) {
      return nameParts[0][0]?.toUpperCase() || 'U';
    }
    return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
  };

  const initials = getUserInitials();

  const handleSignOut = async () => {
    await utilHandleSignOut();
  };

  const getSubscriptionBadge = () => {
    if (result.isAdmin) {
      return <Badge variant="default" className="bg-primary text-primary-foreground">Admin - Unlimited</Badge>;
    }
    if (result.paymentStatus === 'paid') {
      return <Badge variant="default" className="bg-compass text-compass-foreground">Pro Member</Badge>;
    }
    return <Badge variant="secondary">Free Tier</Badge>;
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            className="relative h-10 gap-2 rounded-full hover:bg-muted/50 px-3"
            aria-label={`Open account menu for ${displayName}`}
            data-testid="user-profile-menu-trigger"
          >
            <Avatar className="h-8 w-8">
              <AvatarImage src={profileData?.avatar_url} alt="" />
              <AvatarFallback className="bg-primary/10 text-primary text-sm" aria-hidden="true">
                {initials}
              </AvatarFallback>
            </Avatar>
            <span className="hidden md:inline-block text-sm font-medium max-w-[120px] truncate">
              {displayName}
            </span>
            <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-80 bg-popover">
          {/* Enhanced Header with User Details */}
          <div className="flex items-start gap-3 p-4">
            <Avatar className="h-14 w-14">
              <AvatarImage src={profileData?.avatar_url} alt={displayName} />
              <AvatarFallback className="bg-primary/10 text-primary text-lg">
                {initials}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 min-w-0 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold truncate">{displayName}</p>
                {canAccessAdmin && (
                  <Badge variant="default" className="h-5 px-1.5 text-[10px] bg-primary/90 text-primary-foreground">
                    Admin
                  </Badge>
                )}
                {activeRole && adminRoles.length > 1 && (
                  <RoleBadge role={activeRole} />
                )}
              </div>
              <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                <Mail className="h-3 w-3" />
                {result.userEmail}
              </p>
              
              {profileData?.current_job_title && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <BriefcaseIcon className="h-3 w-3" />
                  {profileData.current_job_title}
                  {profileData.career_stage && ` • ${profileData.career_stage}`}
                </p>
              )}
              
              <div className="pt-1">
                {getSubscriptionBadge()}
              </div>
            </div>
          </div>

          {/* Profile Completion */}
          {profileCompletion && profileCompletion.weightedScore < 100 && (
            <div className="px-4 pb-3">
              <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium">Profile Completion</span>
                  <span className="font-semibold text-primary">{profileCompletion.weightedScore}%</span>
                </div>
                <Progress value={profileCompletion.weightedScore} className="h-1.5" />
                <p className="text-xs text-muted-foreground">
                  {profileCompletion.nextSteps[0]}
                </p>
              </div>
            </div>
          )}
          
          <DropdownMenuSeparator />
          
          {/* Main Menu Items */}
          <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
            <User className="mr-2 h-4 w-4" />
            <span>View Full Profile</span>
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
            <Settings className="mr-2 h-4 w-4" />
            <span>Settings</span>
          </DropdownMenuItem>

          {/* Career stage lifecycle - only for school/college users who can advance */}
          {(profileData?.user_segment === 'school_student' || profileData?.user_segment === 'college_student') && (
            <DropdownMenuItem
              onSelect={() => {
                // Snapshot current location + journey so user can return
                import('@/utils/segmentRouting').then(({ buildLifecycleHubPath, snapshotJourneyBeforeLifecycle, nextSegmentFor }) => {
                  snapshotJourneyBeforeLifecycle(location.pathname);
                  navigate(buildLifecycleHubPath(profileData?.user_segment, nextSegmentFor(profileData?.user_segment)));
                });
              }}
              className="cursor-pointer"
              aria-label={`Update career stage. Currently ${profileData.user_segment === 'school_student' ? 'school' : 'college'}.`}
            >
              <GraduationCap className="mr-2 h-4 w-4" aria-hidden="true" />
              <span className="flex-1">Update career stage</span>
              <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
                {profileData.user_segment === 'school_student' ? 'School' : 'College'}
              </Badge>
            </DropdownMenuItem>
          )}
          
          {/* Admin Hub - Quick access for admins */}
          {canAccessAdmin && (
            <DropdownMenuItem 
              onClick={() => handleViewSwitch('/admin')} 
              className="cursor-pointer"
            >
              <LayoutDashboard className="mr-2 h-4 w-4" />
              <span className="flex-1">Admin Hub</span>
              <span className="text-[10px] text-muted-foreground ml-2">Alt+A</span>
            </DropdownMenuItem>
          )}

          {/* Product Switching Section */}
          <ProductSwitchSection 
            canAccessAdmin={canAccessAdmin}
            isAdmin={isAdmin}
            isSuperAdmin={isSuperAdmin}
            navigate={navigate}
            isValidUser={result?.isValid === true}
          />

          {/* Role Switcher - Only show when roles loaded and user has admin roles */}
          {!rolesLoading && adminRoles.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground font-normal px-2 py-1.5">
                SWITCH ROLE
              </DropdownMenuLabel>
              <div className="px-1 pb-1">
                {adminRoles.map((role) => {
                  const config = roleConfig[role] || roleConfig.user;
                  const Icon = config.icon;
                  const isActive = role === activeRole;
                  
                  return (
                    <DropdownMenuItem
                      key={role}
                      onClick={() => {
                        setActiveRole(role);
                        // When selecting an admin role, navigate to admin panel
                        if (['super_admin', 'admin', 'it_admin', 'platform_admin'].includes(role)) {
                          navigate('/admin');
                        }
                      }}
                      className={cn("cursor-pointer gap-3", isActive && "bg-primary/5")}
                    >
                      <div className={cn("p-1.5 rounded", config.color)}>
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex-1">
                        <span className="text-sm">{config.label}</span>
                      </div>
                      {isActive && <Check className="h-4 w-4 text-primary" />}
                    </DropdownMenuItem>
                  );
                })}
              </div>
            </>
          )}
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem 
            onClick={() => setShowSignOutDialog(true)} 
            className="cursor-pointer text-destructive focus:text-destructive"
          >
            <LogOut className="mr-2 h-4 w-4" />
            <span>Sign out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={showSignOutDialog} onOpenChange={setShowSignOutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign out?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to sign out? You'll need to log in again to continue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSignOut} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Sign Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
